import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import Swal from "sweetalert2";
import { fetchClientCreditInvoices, fetchStoredFileUrl, uploadCreditInvoice } from "../gestionCarteraApi";
import { creditInvoiceKeys, creditInvoiceOptionLabel, hasCreditInvoice, invoiceableCredits } from "../creditInvoiceCatalog";
import { formatDate } from "../gestionCarteraFormat";
import { cellSx, COLORS, headCellSx, PrimaryButton, SecondaryButton, StatusBadge } from "../gestionCarteraUi";

const MAX_INVOICE_MB = 10;
const MAX_INVOICE_BYTES = MAX_INVOICE_MB * 1024 * 1024;
/** Spring responde este texto cuando no expone el mensaje del error; en ese caso se usa el genérico. */
const HIDDEN_SERVER_MESSAGE = "No message available";
const INVOICE_COLUMNS = ["Crédito", "Estado", "Originado", "Factura"];
const subtitleSx = { fontSize: 14, fontWeight: 700, color: COLORS.muted };

const uploadErrorText = (uploadError) => {
  if (uploadError?.response?.status === 413) return `El archivo supera el tamaño máximo permitido de ${MAX_INVOICE_MB} MB.`;
  const serverMessage = uploadError?.response?.data?.message;
  return serverMessage && serverMessage !== HIDDEN_SERVER_MESSAGE ? serverMessage : "Error al cargar la factura.";
};

const confirmReplace = (credit) =>
  Swal.fire({
    icon: "warning",
    title: "Reemplazar factura",
    text: `El crédito ${credit.oldId ?? "—"} ya tiene factura. ¿Quieres reemplazarla?`,
    showCancelButton: true,
    confirmButtonText: "Reemplazar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: COLORS.primary,
  }).then((result) => result.isConfirmed);

/** URL firmada de cada factura; `null` si S3 no la pudo resolver. */
const fetchInvoiceUrls = async (credits) => {
  const keys = creditInvoiceKeys(credits);
  const urls = await Promise.all(keys.map((key) => fetchStoredFileUrl(key).catch(() => null)));
  return Object.fromEntries(keys.map((key, index) => [key, urls[index]]));
};

/** Todos los créditos del cliente con su factura, la URL de cada archivo y el crédito elegido para cargar. */
const useClientCreditInvoices = (clientUuid) => {
  const [state, setState] = useState({ status: "loading", credits: [], urls: {} });
  const [selectedUuid, setSelectedUuid] = useState("");

  const load = useCallback(
    (keepSelection) => {
      setState((current) => ({ ...current, status: "loading" }));
      fetchClientCreditInvoices(clientUuid)
        .then(async (credits) => {
          const list = Array.isArray(credits) ? credits : [];
          setState({ status: "ready", credits: list, urls: await fetchInvoiceUrls(list) });
          const uploadable = invoiceableCredits(list);
          setSelectedUuid((current) =>
            keepSelection && uploadable.some((credit) => credit.uuid === current) ? current : uploadable[0]?.uuid || ""
          );
        })
        .catch(() => setState({ status: "error", credits: [], urls: {} }));
    },
    [clientUuid]
  );

  useEffect(() => {
    if (clientUuid) load(false);
  }, [clientUuid, load]);

  const uploadable = invoiceableCredits(state.credits);
  const selected = uploadable.find((credit) => credit.uuid === selectedUuid) || null;

  return { state, uploadable, selected, setSelectedUuid, reload: () => load(true) };
};

function InvoiceCell({ credit, urls }) {
  if (credit.invoiceName) {
    const url = urls[credit.invoiceName];
    if (!url) return <StatusBadge tone="yellow" label="No se pudo abrir" />;
    return (
      <PrimaryButton component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ minHeight: 34, px: 1.5 }}>
        Ver factura
      </PrimaryButton>
    );
  }
  if (credit.facturaEnviada === true) return <StatusBadge tone="yellow" label="Enviada sin archivo" />;
  return <StatusBadge tone="gray" label="Sin factura" />;
}

function CreditInvoicesTable({ credits, urls }) {
  if (credits.length === 0) {
    return <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted }}>Este cliente no tiene créditos.</Typography>;
  }
  return (
    <TableContainer sx={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: "12px", backgroundColor: "#fff" }}>
      <Table size="small" aria-label="Facturas por crédito" sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>{INVOICE_COLUMNS.map((column) => <TableCell key={column} sx={{ ...headCellSx, borderTop: 0 }}>{column}</TableCell>)}</TableRow>
        </TableHead>
        <TableBody>
          {credits.map((credit) => (
            <TableRow key={credit.uuid}>
              <TableCell sx={cellSx}>{credit.oldId ?? "—"}</TableCell>
              <TableCell sx={cellSx}>{credit.status || "—"}</TableCell>
              <TableCell sx={cellSx}>{formatDate(credit.originDate)}</TableCell>
              <TableCell sx={cellSx}><InvoiceCell credit={credit} urls={urls} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function UploadInvoice({ clientUuid, uploadable, selected, onSelect, onUploaded }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const chooseFile = (event) => {
    const chosen = event.target.files?.[0];
    if (chosen && chosen.size > MAX_INVOICE_BYTES) {
      clearFile();
      Swal.fire({ icon: "warning", title: "Archivo muy grande", text: `El archivo supera el tamaño máximo permitido de ${MAX_INVOICE_MB} MB.`, confirmButtonColor: COLORS.primary });
      return;
    }
    setFile(chosen || null);
  };

  const save = async () => {
    if (!file || !selected) return;
    if (hasCreditInvoice(selected) && !(await confirmReplace(selected))) return;
    setSaving(true);
    try {
      await uploadCreditInvoice(clientUuid, selected.uuid, file);
      clearFile();
      Swal.fire({ icon: "success", title: "Factura cargada", text: `Factura cargada al crédito ${selected.oldId ?? "—"}.`, confirmButtonColor: COLORS.primary });
      onUploaded();
    } catch (uploadError) {
      Swal.fire({ icon: "error", title: "Error", text: uploadErrorText(uploadError), confirmButtonColor: COLORS.primary });
    } finally {
      setSaving(false);
    }
  };

  if (uploadable.length === 0) {
    return (
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted }}>
        Este cliente no tiene créditos APPROVED o COMPLETED para asignar factura.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <TextField
        select
        size="small"
        label="Crédito"
        value={selected?.uuid || ""}
        onChange={(event) => {
          onSelect(event.target.value);
          clearFile();
        }}
        disabled={saving}
        helperText="Solo créditos APPROVED o COMPLETED"
      >
        {uploadable.map((credit) => (
          <MenuItem key={credit.uuid} value={credit.uuid}>{creditInvoiceOptionLabel(credit)}</MenuItem>
        ))}
      </TextField>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={chooseFile} />
        <SecondaryButton onClick={() => fileRef.current?.click()} disabled={saving || !selected}>Cargar documento</SecondaryButton>
        <PrimaryButton onClick={save} disabled={saving || !file || !selected}>{saving ? "Guardando…" : "Guardar"}</PrimaryButton>
        {file && <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted }}>{file.name}</Typography>}
      </Box>
    </Box>
  );
}

/**
 * Factura del comercio por crédito: la tabla muestra la de cada crédito del cliente (cualquier estado)
 * y debajo se carga la de un crédito elegido, solo APPROVED o COMPLETED, con confirmación si ya tiene.
 */
export default function CreditInvoicePanel({ clientUuid, onSaved }) {
  const { state, uploadable, selected, setSelectedUuid, reload } = useClientCreditInvoices(clientUuid);

  if (state.status === "loading" && state.credits.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>;
  }
  if (state.status === "error") {
    return (
      <Box role="alert" sx={{ textAlign: "center", py: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.primary }}>No pudimos cargar las facturas del cliente</Typography>
        <SecondaryButton sx={{ mt: 2 }} onClick={reload}>Reintentar</SecondaryButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography component="h3" sx={subtitleSx}>Facturas por crédito</Typography>
        <CreditInvoicesTable credits={state.credits} urls={state.urls} />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography component="h3" sx={subtitleSx}>Cargar factura</Typography>
        <UploadInvoice
          clientUuid={clientUuid}
          uploadable={uploadable}
          selected={selected}
          onSelect={setSelectedUuid}
          onUploaded={() => {
            reload();
            onSaved?.();
          }}
        />
      </Box>
    </Box>
  );
}
